import { Location } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { OrganizationService } from 'src/app/services/organization/organization.service';
import { UserService } from 'src/app/services/user/user.service';
import { OrgUrlState, splitOrgPrefix } from './org-url';

/**
 * Keeps the address bar and the tab's organization in step (see OrgUrlState):
 * - the org this tab loads is the one its URL names (UserService reads
 *   `requested` first), and once it is loaded the address bar carries it;
 * - Back / Forward onto another org's URL switches this tab to that org in
 *   place — the page the history entry points at stays where it is.
 * Started once by AppComponent.
 */
@Injectable({ providedIn: 'root' })
export class OrgUrlSyncService {
  private readonly location = inject(Location);
  private readonly organization = inject(OrganizationService);
  private readonly router = inject(Router);
  private readonly state = inject(OrgUrlState);
  private readonly user = inject(UserService);
  private subs: Subscription[] = [];

  constructor() {
    inject(DestroyRef).onDestroy(() => this.subs.forEach((s) => s.unsubscribe()));
  }

  public start(): void {
    if (this.subs.length) return;
    this.subs.push(this.organization.orgId$.subscribe((orgId) => {
      if (!orgId || orgId === this.state.active()) return;
      this.state.active.set(orgId);
      this.state.requested.set(orgId);
      // Rewrite the current entry so the address bar names the org now loaded.
      this.location.replaceState(this.location.path(true));
    }));
    this.subs.push(this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart && e.navigationTrigger === 'popstate'),
    ).subscribe(() => {
      const target = splitOrgPrefix(window.location.pathname).orgId;
      const member = (this.user.data?.organizationIds ?? []).includes(target ?? '');
      if (target && member && target !== this.state.active()) void this.user.setCurrentOrganization(target);
    }));
  }
}
